import { all, call, select, takeEvery, put } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';

import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { waitForModal } from 'components/Modal';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import URLS from 'constants/urls';
import { transformKeysToSnakeCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenueId, selectRouteParams } from 'selectors/general';
import { services, practitioners, resources, scheduleIncrements } from 'store/saga/entities/bound';

import { goToTab } from '../ManageServices.saga';
import { messages } from './Services.messages';
import { selectServices } from './Services.selectors';
import { refreshServicesContext } from './ServicesContext/ServicesContext';

export const FETCH_SERVICES = 'FETCH_SERVICES';
export const fetchServices = () => ({ type: FETCH_SERVICES });

export const FETCH_SERVICES_PRACTITIONERS_RESOURCES = 'FETCH_SERVICES';
export const fetchServicesPractitionersResources = () => ({ type: FETCH_SERVICES_PRACTITIONERS_RESOURCES });

export const CREATE_SERVICE = 'CREATE_SERVICE';
export const createService = (payload, formAsyncFns) => ({
  type: CREATE_SERVICE,
  payload,
  formAsyncFns,
});

export const UPDATE_SERVICE = 'UPDATE_SERVICE';
export const updateService = (payload, formAsyncFns) => ({
  type: UPDATE_SERVICE,
  payload,
  formAsyncFns,
});

export const UPDATE_SELECTED_SERVICE = 'UPDATE_SELECTED_SERVICE';
export const updateSelectedService = (id, focus) => ({ type: UPDATE_SELECTED_SERVICE, id, focus });

export const DELETE_SERVICE = 'DELETE_SERVICE';
export const deleteService = id => ({
  type: DELETE_SERVICE,
  id,
});

export const RESET_SCHEDULE_INCREMENTS_FORM = 'RESET_SCHEDULE_INCREMENTS_FORM';
export const resetScheduleIncrementsForm = resetFormFunction => ({
  type: RESET_SCHEDULE_INCREMENTS_FORM,
  resetFormFunction,
});

export const UPDATE_SCHEDULE_INCREMENTS = 'UPDATE_SCHEDULE_INCREMENTS';
export const updateScheduleIncrements = (payload, formAsyncFns) => ({
  type: UPDATE_SCHEDULE_INCREMENTS,
  payload,
  formAsyncFns,
});

export function* doFetchServices(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    return yield call(fetchEntity, services, [client, venueId]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doFetchServicesPractitionersResources(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    return yield all([
      call(fetchEntity, services, [client, venueId]),
      call(fetchEntity, practitioners, [client, venueId]),
      call(fetchEntity, resources, [client, venueId]),
    ]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doUpdateSelectedService(client, action) {
  try {
    const { id, focus } = action;
    const routeParams = yield select(selectRouteParams);
    const newPath = makePath.manageServices({
      ...routeParams,
      itemId: id,
      focus,
    });
    yield call(client.navigate, newPath);
  } catch (error) {
    reportError(error);
  }
}

export function* doDeleteService(client, action) {
  const { httpClient } = client;
  const { id } = action;

  try {
    const { venueId, tab } = yield select(selectRouteParams);
    const endpoint = URLS.manageServices.services.delete(venueId, id);
    const confirmed = yield call(waitForModal, client, 'DeleteServiceModal');

    if (confirmed) {
      yield call([httpClient, httpClient.delete], endpoint);

      yield all([
        put(fetchServices()),
        call(fetchEntity, practitioners, [client, venueId]),
        call(fetchEntity, resources, [client, venueId]),
        put(createInfoBanner(messages.success.serviceDeleted)),
        put(goToTab(tab)),
      ]);
      refreshServicesContext();
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doResetScheduleIncrementForm(client, action) {
  const { resetFormFunction } = action;

  const confirmed = yield call(waitForModal, client, 'ResetScheduleIncrementsModal');

  if (confirmed) {
    yield call(resetFormFunction);
  }
}

export function* doUpdateScheduleIncrements(client, action) {
  const { httpClient } = client;
  const { payload, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;
  const { serviceIds, increments } = payload;

  try {
    const { venueId, tab } = yield select(selectRouteParams);
    const servicesList = yield select(selectServices);

    const confirmed = yield call(waitForModal, client, 'UpdateIncrementsConfirmationModal', {
      serviceIds,
      increments,
      services: servicesList,
    });

    if (!confirmed) {
      return;
    }

    const endpoint = URLS.manageServices.scheduleIncrements.update(venueId);
    const endpointPayload = { msiServiceIds: serviceIds, scheduleIncrements: increments };

    yield call([httpClient, httpClient.post], endpoint, transformKeysToSnakeCase(endpointPayload));
    yield call(resolveSubmit);
    yield put(goToTab(tab));
    yield put(createSuccessSnackbar(messages.success.changesSaved));
  } catch (error) {
    yield all([
      put(createErrorSnackbar(messages.error.problemWithRequest)),
      call(rejectSubmit),
      call(reportError, error),
    ]);
  }
}

export function* handleScheduleIncrementsRouteEnter(client) {
  const venueId = yield select(selectActiveVenueId);
  yield call(fetchEntityIfNeeded, services, [client, venueId]);

  const servicesList = yield select(selectServices);
  const serviceIds = servicesList.map(({ id }) => id);

  yield call(fetchEntity, scheduleIncrements, [client, { venueId, serviceIds }]);
}

export function* watchActions(client) {
  yield takeEvery(FETCH_SERVICES, doFetchServices, client);
  yield takeEvery(FETCH_SERVICES_PRACTITIONERS_RESOURCES, doFetchServicesPractitionersResources, client);
  yield takeEvery(UPDATE_SELECTED_SERVICE, doUpdateSelectedService, client);
  yield takeEvery(DELETE_SERVICE, doDeleteService, client);
  yield takeEvery(RESET_SCHEDULE_INCREMENTS_FORM, doResetScheduleIncrementForm, client);
  yield takeEvery(UPDATE_SCHEDULE_INCREMENTS, doUpdateScheduleIncrements, client);
}
